import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useSignalR } from '@/contexts/signalR';
import { Accordion } from '@tw/tw-component-library';
import { useWorkspaceRelayHubHandlers } from '@/hooks/signalR/useWorkspaceRelayHubHandlers';
import { WorkspaceRelayHubEvents } from '@/handlers/signalR/workspaceRelayHub';
import { useUserRelayHubHandlers } from '@/hooks/signalR/useUserRelayHubHandlers';
import { UserRelayHubEvents } from '@/handlers/signalR/userRelayHub';
import { useAskRelayHubHandlers } from '@/hooks/signalR/useAskRelayHubHandlers';
import { AskRelayHubEvents } from '@/handlers/signalR/askRelayHub';
import { useWorkspaceErrorHandler } from '@/hooks/useWorkspaceErrorHandler';
import { UseAuth } from '@/contexts/AuthContext';
import {
  addClientToGroupUserSession,
  sendMessageUserSession,
  sendUserTypingStateUserSession,
} from '@/apis/core/messaging/userMessageRelayHub';
import {
  addClientToGroupWorkflowSession,
  sendMessageWorkflowSession,
  sendUserTypingStateWorkflowSession,
} from '@/apis/core/messaging/workflowMessageRelayHub';
import { addClientToGroupWorkspaceSession } from '@/apis/core/messaging/workspaceMessageRelayHub';

// ============= Utilities =============
/** Extract response/body from unknown error for Azure/API parsing */
function getResponseFromError(err: unknown): unknown {
  const e = err as { response?: unknown; body?: unknown };
  return e?.response ?? e?.body;
}

// ============= Styled Components =============
const PageContainer = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Section = styled.div`
  margin-bottom: 32px;
  padding: 24px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  margin: 0 0 16px 0;
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
`;

const ConnectionStatus = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isConnected',
})<{ isConnected: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 6px;
  background: ${({ isConnected }) => (isConnected ? '#d1fae5' : '#fee2e2')};
  color: ${({ isConnected }) => (isConnected ? '#065f46' : '#991b1b')};
  font-weight: 500;
  margin-bottom: 24px;
`;

const StatusDot = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isConnected',
})<{ isConnected: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ isConnected }) => (isConnected ? '#10b981' : '#ef4444')};
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  transition: border-color 0.2s;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ variant }) =>
    variant === 'primary' ? '#3b82f6' : '#6b7280'};
  color: white;

  &:hover:not(:disabled) {
    background: ${({ variant }) =>
      variant === 'primary' ? '#2563eb' : '#4b5563'};
    transform: translateY(-1px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  & + & {
    margin-left: 8px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const MessageLog = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  max-height: 400px;
  overflow-y: auto;
  font-family: 'Courier New', monospace;
  font-size: 12px;
`;

const MessageEntry = styled.div`
  padding: 8px;
  margin-bottom: 8px;
  background: white;
  border-left: 3px solid #3b82f6;
  border-radius: 4px;
`;

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-weight: 600;
  color: #1f2937;
`;

const MessageTime = styled.span`
  color: #6b7280;
  font-size: 11px;
  font-weight: normal;
`;

const MessageContent = styled.pre`
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  color: #374151;
  font-size: 11px;
`;

// ============= Component =============
export const SignalRTestPage: React.FC = () => {
  const { invokeHub, isConnected, getConnectionState, getConnection } =
    useSignalR();
  const { handleError, handleSuccess, handleWarning, handleInfo } =
    useWorkspaceErrorHandler();
  const { currentUser } = UseAuth();

  // Error logging state
  const [errors, setErrors] = useState<string[]>([]);

  // User Message Relay Hub
  const [userWorkspaceId, setUserWorkspaceId] = useState('');
  const [userSenderId, setUserSenderId] = useState('');
  const [userMessage, setUserMessage] = useState('');
  const [userTypingUserId, setUserTypingUserId] = useState('');
  const [userIsTyping, setUserIsTyping] = useState(false);

  // Workspace Message Relay Hub
  const [workspaceWorkspaceId, setWorkspaceWorkspaceId] = useState('');
  const [workspaceSenderId, setWorkspaceSenderId] = useState('');
  const [workspaceMessage, setWorkspaceMessage] = useState('');
  const [workspaceTypingUserId, setWorkspaceTypingUserId] = useState('');
  const [workspaceIsTyping, setWorkspaceIsTyping] = useState(false);

  // Workspace Relay Hub (for DocumentsPage events)
  const [workspaceRelayWorkspaceId, setWorkspaceRelayWorkspaceId] =
    useState('');
  const [workspaceRelayMessages, setWorkspaceRelayMessages] = useState<
    Array<{
      event: string;
      timestamp: string;
      data: any;
      parsed?: {
        workspaceId?: string;
        senderId?: string;
        message?: string;
        rawArguments?: any[];
        argumentCount?: number;
        [key: string]: any;
      };
    }>
  >([]);

  // Ask Relay Hub (for ask session message-level events)
  const [askWorkspaceIdFilter, setAskWorkspaceIdFilter] = useState('');
  const [askSessionIdFilter, setAskSessionIdFilter] = useState('');
  const [askRelayMessages, setAskRelayMessages] = useState<
    Array<{
      event: string;
      timestamp: string;
      data: any;
      parsed?: {
        workspaceId?: string;
        askSessionId?: string;
        messageId?: string;
        intent?: string;
        tokens?: number;
        [key: string]: any;
      };
    }>
  >([]);

  // User Relay Hub (for workspace/user management events)
  const [userRelayWorkspaceId, setUserRelayWorkspaceId] = useState('');
  const [userRelayMessages, setUserRelayMessages] = useState<
    Array<{
      event: string;
      timestamp: string;
      data: any;
      parsed?: {
        workspaceId?: string;
        userId?: string;
        userName?: string;
        workspaceName?: string;
        workspaceData?: any;
        userData?: any;
        [key: string]: any;
      };
    }>
  >([]);

  // Generic custom hub invoke
  const [customHubName, setCustomHubName] = useState<string>('workspaceRelayHub');
  const [customMethodName, setCustomMethodName] = useState<string>('');
  const [customArgsText, setCustomArgsText] = useState<string>('[]');
  const [customInvokeResult, setCustomInvokeResult] = useState<string | null>(
    null
  );
  const [customInvokeError, setCustomInvokeError] = useState<string | null>(
    null
  );
  const [isInvokingCustom, setIsInvokingCustom] = useState<boolean>(false);

  // Set default values from current user
  useEffect(() => {
    if (currentUser) {
      const userId =
        (currentUser as any).userId || (currentUser as any).userEmail || '';
      setUserSenderId(userId);
      setUserTypingUserId(userId);
      setWorkspaceSenderId(userId);
      setWorkspaceTypingUserId(userId);
    }
  }, [currentUser]);

  // Use correct hub names that match SignalRContext
  // Note: API files use 'usermessagerelayhub' but SignalRContext uses 'userRelayHub'
  // We'll use 'userRelayHub' to match the actual connection
  const userHubConnected = isConnected('userRelayHub');
  const workspaceHubConnected = isConnected('workflowRelayHub');
  const askHubConnected = isConnected('askRelayHub');
  const workspaceRelayHubConnected = isConnected('workspaceRelayHub');

  // Set up error logging for all hubs
  useEffect(() => {
    const userConnection = getConnection('userRelayHub');
    const workflowConnection = getConnection('workflowRelayHub');
    const askConnection = getConnection('askRelayHub');
    const workspaceRelayConnection = getConnection('workspaceRelayHub');

    const setupErrorLogging = (connection: any, hubName: string) => {
      if (!connection) return;

      connection.onclose((error: any) => {
        if (error) {
          const errorMsg = `[${hubName}] Connection closed: ${error.message}`;
          const err =
            error instanceof Error ? error : new Error(errorMsg);
          console.error(errorMsg);
          setErrors((prev) => [
            ...prev,
            `${errorMsg} at ${new Date().toLocaleTimeString()}`,
          ]);
          handleError(err, `${hubName} connection closed`, getResponseFromError(error));
        }
      });

      connection.onreconnecting((error: any) => {
        if (error) {
          const errorMsg = `[${hubName}] Reconnecting: ${error.message}`;
          console.warn(errorMsg);
          setErrors((prev) => [
            ...prev,
            `${errorMsg} at ${new Date().toLocaleTimeString()}`,
          ]);
        }
      });

      connection.onreconnected((connectionId: string) => {
        console.log(`[${hubName}] Reconnected: ${connectionId}`);
        handleSuccess(`${hubName} reconnected successfully`);
      });
    };

    setupErrorLogging(userConnection, 'User Relay Hub');
    setupErrorLogging(workflowConnection, 'Workflow Relay Hub');
    setupErrorLogging(askConnection, 'Ask Relay Hub');
    setupErrorLogging(workspaceRelayConnection, 'Workspace Relay Hub');
  }, [getConnection, handleError, handleSuccess]);

  // Subscribe to workspaceRelayHub events with message logging
  const addMessage = (event: string, data: any, parsed?: any) => {
    setWorkspaceRelayMessages((prev) =>
      [
        {
          event,
          timestamp: new Date().toLocaleTimeString(),
          data,
          parsed,
        },
        ...prev,
      ].slice(0, 100)
    );
  };

  useWorkspaceRelayHubHandlers({
    workspaceId: workspaceRelayWorkspaceId || undefined,
    onEventReceived: (event, data, parsed) => {
      addMessage(event, data, parsed);
    },
    enableToasts: true,
    enableLogging: true,
  });

  // Subscribe to askRelayHub events with message logging
  const addAskRelayMessage = (event: string, data: any, parsed?: any) => {
    setAskRelayMessages((prev) =>
      [
        {
          event,
          timestamp: new Date().toLocaleTimeString(),
          data,
          parsed,
        },
        ...prev,
      ].slice(0, 100)
    );
  };

  useAskRelayHubHandlers({
    workspaceId: askWorkspaceIdFilter || undefined,
    askSessionId: askSessionIdFilter || undefined,
    onEventReceived: (event, data, parsed) => {
      addAskRelayMessage(event, data, parsed);
    },
    enableToasts: true,
    enableLogging: true,
  });

  // Subscribe to userRelayHub events with message logging
  const addUserRelayMessage = (event: string, data: any, parsed?: any) => {
    setUserRelayMessages((prev) =>
      [
        {
          event,
          timestamp: new Date().toLocaleTimeString(),
          data,
          parsed,
        },
        ...prev,
      ].slice(0, 100)
    );
  };

  useUserRelayHubHandlers({
    workspaceId: userRelayWorkspaceId || undefined,
    onEventReceived: (event, data, parsed) => {
      addUserRelayMessage(event, data, parsed);
    },
    enableToasts: true,
    enableLogging: true,
  });

  // ============= Custom Hub Invoke =============

  const handleCustomInvoke = useCallback(async () => {
    setCustomInvokeError(null);
    setCustomInvokeResult(null);

    if (!customHubName.trim()) {
      handleWarning('Hub name is required.');
      return;
    }

    if (!customMethodName.trim()) {
      handleWarning('Method name is required.');
      return;
    }

    let args: unknown[] = [];
    if (customArgsText.trim().length > 0) {
      try {
        const parsed = JSON.parse(customArgsText);
        if (Array.isArray(parsed)) {
          args = parsed;
        } else {
          args = [parsed];
        }
      } catch {
        handleWarning(
          'Invalid JSON for arguments. Use a JSON array (e.g. ["workspace-id","msg"]) or an object.'
        );
        return;
      }
    }

    try {
      setIsInvokingCustom(true);
      handleInfo(
        `Invoking [${customHubName}].${customMethodName} with args: ${
          customArgsText || '[]'
        }`
      );

      const result = await invokeHub<any>(
        customHubName,
        customMethodName,
        ...(args as any[])
      );
      const asString = JSON.stringify(result, null, 2);
      setCustomInvokeResult(asString);
      console.log(
        `🛰️ Custom invoke result [${customHubName}].${customMethodName}:`,
        result
      );
      handleSuccess('Custom hub invocation succeeded');
    } catch (err) {
      const error =
        err instanceof Error
          ? err
          : new Error('Custom hub invocation failed');
      const response = getResponseFromError(err);
      setCustomInvokeError(error.message);
      console.error(
        `❌ Custom invoke error [${customHubName}].${customMethodName}:`,
        error
      );
      handleError(
        error,
        `Custom hub invocation [${customHubName}].${customMethodName}`,
        response
      );
    } finally {
      setIsInvokingCustom(false);
    }
  }, [
    customHubName,
    customMethodName,
    customArgsText,
    invokeHub,
    handleError,
    handleSuccess,
    handleWarning,
    handleInfo,
  ]);

  // User Message Relay Hub Functions
  const handleAddUserClientToGroup = async () => {
    if (!userWorkspaceId.trim()) {
      handleWarning('Please enter a workspace ID');
      return;
    }

    try {
      handleInfo('Adding client to UserMessageRelayHub group...');
      await addClientToGroupUserSession({
        invokeHub,
        userId: userWorkspaceId,
      });
      handleSuccess(`Successfully added to workspace group: ${userWorkspaceId}`);
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to add to group');
      const response = getResponseFromError(err);
      console.error('Failed to add to UserMessageRelayHub group:', error);
      setErrors((prev) => [
        ...prev,
        `[User Hub] ${error.message} at ${new Date().toLocaleTimeString()}`,
      ]);
      handleError(error, 'Failed to add to UserMessageRelayHub group', response);
    }
  };

  const handleSendUserMessage = async () => {
    if (!userWorkspaceId.trim() || !userSenderId.trim() || !userMessage.trim()) {
      handleWarning('Please fill in all required fields');
      return;
    }

    try {
      handleInfo('Sending message via UserMessageRelayHub...');
      await sendMessageUserSession({
        invokeHub,
        workspaceId: userWorkspaceId,
        senderId: userSenderId,
        message: userMessage,
      });
      handleSuccess('Message sent successfully!');
      setUserMessage('');
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to send message');
      const response = getResponseFromError(err);
      console.error('Failed to send message via UserMessageRelayHub:', error);
      setErrors((prev) => [
        ...prev,
        `[User Hub] ${error.message} at ${new Date().toLocaleTimeString()}`,
      ]);
      handleError(error, 'Failed to send message via UserMessageRelayHub', response);
    }
  };

  const handleSendUserTypingState = async () => {
    if (!userWorkspaceId.trim() || !userTypingUserId.trim()) {
      handleWarning('Please enter workspace ID and user ID');
      return;
    }

    try {
      handleInfo(
        `Sending typing state: ${userIsTyping ? 'typing' : 'not typing'}...`
      );
      await sendUserTypingStateUserSession({
        invokeHub,
        workspaceId: userWorkspaceId,
        userId: userTypingUserId,
        isTyping: userIsTyping,
      });
      handleSuccess(
        userIsTyping ? 'User is typing' : 'User stopped typing'
      );
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to send typing state');
      const response = getResponseFromError(err);
      console.error('Failed to send typing state via UserMessageRelayHub:', error);
      setErrors((prev) => [
        ...prev,
        `[User Hub] ${error.message} at ${new Date().toLocaleTimeString()}`,
      ]);
      handleError(error, 'Failed to send typing state', response);
    }
  };

  // Workspace Message Relay Hub Functions
  const handleAddWorkspaceClientToGroup = async () => {
    if (!workspaceWorkspaceId.trim()) {
      handleWarning('Please enter a workspace ID');
      return;
    }

    try {
      handleInfo('Adding client to WorkspaceMessageRelayHub group...');
      await addClientToGroupWorkflowSession({
        invokeHub,
        workspaceId: workspaceWorkspaceId,
      });
      handleSuccess(
        `Successfully added to workspace group: ${workspaceWorkspaceId}`
      );
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to add to group');
      const response = getResponseFromError(err);
      console.error('Failed to add to WorkspaceMessageRelayHub group:', error);
      setErrors((prev) => [
        ...prev,
        `[Workflow Hub] ${error.message} at ${new Date().toLocaleTimeString()}`,
      ]);
      handleError(error, 'Failed to add to WorkspaceMessageRelayHub group', response);
    }
  };

  const handleSendWorkspaceMessage = async () => {
    if (
      !workspaceWorkspaceId.trim() ||
      !workspaceSenderId.trim() ||
      !workspaceMessage.trim()
    ) {
      handleWarning('Please fill in all required fields');
      return;
    }

    try {
      handleInfo('Sending message via WorkspaceMessageRelayHub...');
      await sendMessageWorkflowSession({
        invokeHub,
        workflowId: workspaceWorkspaceId,
        senderId: workspaceSenderId,
        message: workspaceMessage,
      });
      handleSuccess('Message sent successfully!');
      setWorkspaceMessage('');
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to send message');
      const response = getResponseFromError(err);
      console.error('Failed to send message via WorkspaceMessageRelayHub:', error);
      setErrors((prev) => [
        ...prev,
        `[Workflow Hub] ${error.message} at ${new Date().toLocaleTimeString()}`,
      ]);
      handleError(error, 'Failed to send message via WorkspaceMessageRelayHub', response);
    }
  };

  const handleSendWorkspaceTypingState = async () => {
    if (!workspaceWorkspaceId.trim() || !workspaceTypingUserId.trim()) {
      handleWarning('Please enter workspace ID and user ID');
      return;
    }

    try {
      handleInfo(
        `Sending typing state: ${
          workspaceIsTyping ? 'typing' : 'not typing'
        }...`
      );
      await sendUserTypingStateWorkflowSession({
        invokeHub,
        workflowId: workspaceWorkspaceId,
        userId: workspaceTypingUserId,
        isTyping: workspaceIsTyping,
      });
      handleSuccess(
        workspaceIsTyping ? 'User is typing' : 'User stopped typing'
      );
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to send typing state');
      const response = getResponseFromError(err);
      console.error('Failed to send typing state via WorkspaceMessageRelayHub:', error);
      setErrors((prev) => [
        ...prev,
        `[Workflow Hub] ${error.message} at ${new Date().toLocaleTimeString()}`,
      ]);
      handleError(error, 'Failed to send typing state', response);
    }
  };

  // Workspace Relay Hub Functions
  const handleAddWorkspaceRelayClientToGroup = async () => {
    if (!workspaceRelayWorkspaceId.trim()) {
      handleWarning('Please enter a workspace ID');
      return;
    }

    try {
      handleInfo('Adding client to WorkspaceRelayHub group...');
      const workspaceRelayUserId =
        (currentUser as { userId?: string })?.userId ??
        (currentUser as { userEmail?: string })?.userEmail ??
        workspaceRelayWorkspaceId;
      await addClientToGroupWorkspaceSession({
        invokeHub,
        workspaceId: workspaceRelayWorkspaceId,
        userId: workspaceRelayUserId,
      });
      handleSuccess(
        `Successfully added to workspace group: ${workspaceRelayWorkspaceId}`
      );
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to add to group');
      const response = getResponseFromError(err);
      console.error('Failed to add to WorkspaceRelayHub group:', error);
      setErrors((prev) => [
        ...prev,
        `[Workspace Relay Hub] ${error.message} at ${new Date().toLocaleTimeString()}`,
      ]);
      handleError(error, 'Failed to add to WorkspaceRelayHub group', response);
    }
  };

  // User Relay Hub Functions (for workspace/user management events)
  const handleAddUserRelayClientToGroup = async () => {
    if (!userRelayWorkspaceId.trim()) {
      handleWarning('Please enter a workspace ID');
      return;
    }

    try {
      handleInfo('Adding client to UserRelayHub group...');
      await addClientToGroupUserSession({
        invokeHub,
        userId: userRelayWorkspaceId,
      });
      handleSuccess(
        `Successfully added to workspace group: ${userRelayWorkspaceId}`
      );
    } catch (err) {
      const error =
        err instanceof Error ? err : new Error('Failed to add to group');
      const response = getResponseFromError(err);
      console.error('Failed to add to UserRelayHub group:', error);
      setErrors((prev) => [
        ...prev,
        `[User Relay Hub] ${error.message} at ${new Date().toLocaleTimeString()}`,
      ]);
      handleError(error, 'Failed to add to UserRelayHub group', response);
    }
  };

  return (
    <PageContainer>
      <h1>SignalR Messaging Hub Test</h1>
      <p style={{ color: '#6b7280', marginBottom: '24px' }}>
        Test the UserMessageRelayHub and WorkspaceMessageRelayHub SignalR
        connections
      </p>

      {/* Connection Status */}
      <Section>
        <SectionTitle>Connection Status</SectionTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <ConnectionStatus isConnected={userHubConnected}>
            <StatusDot isConnected={userHubConnected} />
            User Relay Hub: {userHubConnected ? 'Connected' : 'Disconnected'} (
            {getConnectionState('userRelayHub')})
          </ConnectionStatus>
          <ConnectionStatus isConnected={workspaceHubConnected}>
            <StatusDot isConnected={workspaceHubConnected} />
            Workflow Relay Hub:{' '}
            {workspaceHubConnected ? 'Connected' : 'Disconnected'} (
            {getConnectionState('workflowRelayHub')})
          </ConnectionStatus>
          <ConnectionStatus isConnected={askHubConnected}>
            <StatusDot isConnected={askHubConnected} />
            Ask Relay Hub: {askHubConnected ? 'Connected' : 'Disconnected'} (
            {getConnectionState('askRelayHub')})
          </ConnectionStatus>
          <ConnectionStatus isConnected={workspaceRelayHubConnected}>
            <StatusDot isConnected={workspaceRelayHubConnected} />
            Workspace Relay Hub:{' '}
            {workspaceRelayHubConnected ? 'Connected' : 'Disconnected'} (
            {getConnectionState('workspaceRelayHub')})
          </ConnectionStatus>
        </div>

        {/* Errors Section */}
        {errors.length > 0 && (
          <div
            style={{
              marginTop: '20px',
              padding: '15px',
              border: '1px solid #ff6b6b',
              borderRadius: '6px',
              backgroundColor: '#ffe0e0',
              maxHeight: '200px',
              overflowY: 'auto',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '10px',
              }}
            >
              <h3 style={{ color: '#d63031', margin: 0, fontSize: '16px' }}>
                Errors ({errors.length})
              </h3>
              <button
                onClick={() => setErrors([])}
                style={{
                  padding: '5px 10px',
                  fontSize: '0.8em',
                  cursor: 'pointer',
                  border: '1px solid #d63031',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: '#d63031',
                }}
              >
                Clear
              </button>
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              {errors.slice(-10).map((error, index) => (
                <li
                  key={index}
                  style={{ marginBottom: '5px', fontSize: '0.9em' }}
                >
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Section>

      {/* User Message Relay Hub */}
      <Accordion
        icon={{
          active: true,
          customIcon: userHubConnected ? (
            <StatusDot isConnected={true} />
          ) : (
            <StatusDot isConnected={false} />
          ),
        }}
        title="UserMessageRelayHub"
        extras={{ defaultExpanded: userHubConnected ? true : false }}
      >
        <Section>
          <SectionTitle>UserMessageRelayHub</SectionTitle>

          <FormGroup>
            <Label>Workspace ID *</Label>
            <Input
              type="text"
              value={userWorkspaceId}
              onChange={(e) => setUserWorkspaceId(e.target.value)}
              placeholder="Enter workspace ID"
            />
          </FormGroup>

          <ButtonGroup>
            <Button
              variant="primary"
              onClick={handleAddUserClientToGroup}
              disabled={!userHubConnected}
            >
              Add Client to Group
            </Button>
          </ButtonGroup>

          <hr
            style={{
              margin: '24px 0',
              border: 'none',
              borderTop: '1px solid #e5e7eb',
            }}
          />

          <FormGroup>
            <Label>Sender ID *</Label>
            <Input
              type="text"
              value={userSenderId}
              onChange={(e) => setUserSenderId(e.target.value)}
              placeholder="Enter sender ID"
            />
          </FormGroup>

          <FormGroup>
            <Label>Message *</Label>
            <TextArea
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              placeholder="Enter message"
            />
          </FormGroup>

          <ButtonGroup>
            <Button
              variant="primary"
              onClick={handleSendUserMessage}
              disabled={!userHubConnected}
            >
              Send Message
            </Button>
          </ButtonGroup>

          <hr
            style={{
              margin: '24px 0',
              border: 'none',
              borderTop: '1px solid #e5e7eb',
            }}
          />

          <FormGroup>
            <Label>User ID *</Label>
            <Input
              type="text"
              value={userTypingUserId}
              onChange={(e) => setUserTypingUserId(e.target.value)}
              placeholder="Enter user ID"
            />
          </FormGroup>

          <FormGroup>
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                checked={userIsTyping}
                onChange={(e) => setUserIsTyping(e.target.checked)}
              />
              <Label style={{ margin: 0, cursor: 'pointer' }}>
                User is typing
              </Label>
            </CheckboxGroup>
          </FormGroup>

          <ButtonGroup>
            <Button
              variant="primary"
              onClick={handleSendUserTypingState}
              disabled={!userHubConnected}
            >
              Send Typing State
            </Button>
          </ButtonGroup>
        </Section>
      </Accordion>

      {/* Workspace Relay Hub */}
      <Accordion
        icon={{
          active: true,
          customIcon: workspaceRelayHubConnected && (
            <StatusDot isConnected={workspaceRelayHubConnected} />
          ),
        }}
        title="WorkspaceRelayHub"
        extras={{ defaultExpanded: !workspaceRelayHubConnected ? true : false }}
      >
        <Section>
          <SectionTitle>WorkspaceRelayHub</SectionTitle>

          <FormGroup>
            <Label>Workspace ID *</Label>
            <Input
              type="text"
              value={workspaceWorkspaceId}
              onChange={(e) => setWorkspaceWorkspaceId(e.target.value)}
              placeholder="Enter workspace ID"
            />
          </FormGroup>

          <ButtonGroup>
            <Button
              variant="primary"
              onClick={handleAddWorkspaceClientToGroup}
              disabled={workspaceHubConnected}
            >
              Add Client to Group
            </Button>
          </ButtonGroup>

          <hr
            style={{
              margin: '24px 0',
              border: 'none',
              borderTop: '1px solid #e5e7eb',
            }}
          />

          <FormGroup>
            <Label>Sender ID *</Label>
            <Input
              type="text"
              value={workspaceSenderId}
              onChange={(e) => setWorkspaceSenderId(e.target.value)}
              placeholder="Enter sender ID"
            />
          </FormGroup>

          <FormGroup>
            <Label>Message *</Label>
            <TextArea
              value={workspaceMessage}
              onChange={(e) => setWorkspaceMessage(e.target.value)}
              placeholder="Enter message"
            />
          </FormGroup>

          <ButtonGroup>
            <Button
              variant="primary"
              onClick={handleSendWorkspaceMessage}
              disabled={workspaceHubConnected}
            >
              Send Message
            </Button>
          </ButtonGroup>

          <hr
            style={{
              margin: '24px 0',
              border: 'none',
              borderTop: '1px solid #e5e7eb',
            }}
          />

          <FormGroup>
            <Label>User ID *</Label>
            <Input
              type="text"
              value={workspaceTypingUserId}
              onChange={(e) => setWorkspaceTypingUserId(e.target.value)}
              placeholder="Enter user ID"
            />
          </FormGroup>

          <FormGroup>
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                checked={workspaceIsTyping}
                onChange={(e) => setWorkspaceIsTyping(e.target.checked)}
              />
              <Label style={{ margin: 0, cursor: 'pointer' }}>
                User is typing
              </Label>
            </CheckboxGroup>
          </FormGroup>

          <ButtonGroup>
            <Button
              variant="primary"
              onClick={handleSendWorkspaceTypingState}
              disabled={workspaceHubConnected}
            >
              Send Typing State
            </Button>
          </ButtonGroup>

          <hr
            style={{
              margin: '24px 0',
              border: 'none',
              borderTop: '1px solid #e5e7eb',
            }}
          />

          <FormGroup>
            <SectionTitle>WorkspaceRelayHub (Documents Events)</SectionTitle>
            <p
              style={{
                color: '#6b7280',
                marginBottom: '16px',
                fontSize: '14px',
              }}
            >
              This hub handles workspace document events like file uploads,
              collections, workflows, and asks. Subscribe to events to see
              real-time notifications.
            </p>

            <FormGroup>
              <Label>Workspace ID *</Label>
              <Input
                type="text"
                value={workspaceRelayWorkspaceId}
                onChange={(e) => setWorkspaceRelayWorkspaceId(e.target.value)}
                placeholder="Enter workspace ID"
              />
            </FormGroup>

            <ButtonGroup>
              <Button
                variant="primary"
                onClick={handleAddWorkspaceRelayClientToGroup}
                disabled={!workspaceRelayHubConnected}
              >
                Add Client to Group
              </Button>
              <Button
                variant="secondary"
                onClick={() => setWorkspaceRelayMessages([])}
                disabled={workspaceRelayMessages.length === 0}
              >
                Clear Messages
              </Button>
            </ButtonGroup>

            <hr
              style={{
                margin: '24px 0',
                border: 'none',
                borderTop: '1px solid #e5e7eb',
              }}
            />

            <FormGroup>
              <Label>
                Subscribed Events ({workspaceRelayMessages.length} messages
                received)
              </Label>
              <div
                style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  marginBottom: '8px',
                }}
              >
                Listening for:{' '}
                {Object.values(WorkspaceRelayHubEvents).join(', ')}
              </div>
            </FormGroup>

            {workspaceRelayMessages.length > 0 ? (
              <MessageLog>
                {workspaceRelayMessages.map((msg, index) => {
                  return (
                    <MessageEntry key={index}>
                      <MessageHeader>
                        <span>{msg.event}</span>
                        <MessageTime>{msg.timestamp}</MessageTime>
                      </MessageHeader>
                      {msg.parsed ? (
                        <div>
                          <div style={{ marginBottom: '8px' }}>
                            <strong>Parsed Data:</strong>
                            <div
                              style={{
                                marginTop: '4px',
                                padding: '8px',
                                background: '#f0f9ff',
                                borderRadius: '4px',
                              }}
                            >
                              {msg.parsed.workspaceId &&
                                msg.parsed.workspaceId !== 'unknown' && (
                                  <div
                                    style={{
                                      marginBottom: '4px',
                                      color: '#1e40af',
                                    }}
                                  >
                                    <strong>Workspace ID:</strong>{' '}
                                    <code
                                      style={{
                                        background: '#dbeafe',
                                        padding: '2px 4px',
                                        borderRadius: '2px',
                                      }}
                                    >
                                      {msg.parsed.workspaceId}
                                    </code>
                                  </div>
                                )}
                              {msg.parsed.senderId &&
                                msg.parsed.senderId !== 'unknown' && (
                                  <div
                                    style={{
                                      marginBottom: '4px',
                                      color: '#7c3aed',
                                    }}
                                  >
                                    <strong>Sender ID:</strong>{' '}
                                    <code
                                      style={{
                                        background: '#ede9fe',
                                        padding: '2px 4px',
                                        borderRadius: '2px',
                                      }}
                                    >
                                      {msg.parsed.senderId}
                                    </code>
                                  </div>
                                )}
                              {msg.parsed.message &&
                                msg.parsed.message !== 'unknown' && (
                                  <div
                                    style={{
                                      marginBottom: '4px',
                                      color: '#059669',
                                    }}
                                  >
                                    <strong>Message:</strong>{' '}
                                    <span
                                      style={{
                                        background: '#d1fae5',
                                        padding: '2px 4px',
                                        borderRadius: '2px',
                                      }}
                                    >
                                      {msg.parsed.message}
                                    </span>
                                  </div>
                                )}
                              {msg.parsed.fileName && (
                                <div
                                  style={{
                                    marginBottom: '4px',
                                    color: '#dc2626',
                                  }}
                                >
                                  <strong>File Name:</strong>{' '}
                                  {msg.parsed.fileName}
                                </div>
                              )}
                              {msg.parsed.fileId && (
                                <div
                                  style={{
                                    marginBottom: '4px',
                                    color: '#dc2626',
                                  }}
                                >
                                  <strong>File ID:</strong>{' '}
                                  <code
                                    style={{
                                      background: '#fee2e2',
                                      padding: '2px 4px',
                                      borderRadius: '2px',
                                    }}
                                  >
                                    {msg.parsed.fileId}
                                  </code>
                                </div>
                              )}
                              {msg.parsed.error && (
                                <div
                                  style={{
                                    marginBottom: '4px',
                                    color: '#dc2626',
                                  }}
                                >
                                  <strong>Error:</strong> {msg.parsed.error}
                                </div>
                              )}
                              {/* Show any other parsed fields */}
                              {Object.entries(msg.parsed).map(
                                ([key, value]) => {
                                  if (
                                    [
                                      'workspaceId',
                                      'senderId',
                                      'message',
                                      'fileName',
                                      'fileId',
                                      'error',
                                      'rawArguments',
                                      'argumentCount',
                                    ].includes(key)
                                  ) {
                                    return null;
                                  }
                                  return (
                                    <div
                                      key={key}
                                      style={{ marginBottom: '4px' }}
                                    >
                                      <strong>{key}:</strong>{' '}
                                      {typeof value === 'object'
                                        ? JSON.stringify(value)
                                        : String(value)}
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          </div>
                          <details style={{ marginTop: '8px' }}>
                            <summary
                              style={{
                                cursor: 'pointer',
                                color: '#6b7280',
                                fontSize: '11px',
                              }}
                            >
                              View Raw Data
                            </summary>
                            <MessageContent style={{ marginTop: '8px' }}>
                              {JSON.stringify(msg.data, null, 2)}
                            </MessageContent>
                          </details>
                        </div>
                      ) : (
                        <MessageContent>
                          {JSON.stringify(msg.data, null, 2)}
                        </MessageContent>
                      )}
                    </MessageEntry>
                  );
                })}
              </MessageLog>
            ) : (
              <div
                style={{
                  padding: '24px',
                  textAlign: 'center',
                  color: '#9ca3af',
                  fontStyle: 'italic',
                }}
              >
                No messages received yet. Join a workspace group and trigger
                events to see them here.
              </div>
            )}
          </FormGroup>
        </Section>
      </Accordion>

      {/* Ask Relay Hub (Ask session events) */}
      <Accordion
        icon={{
          active: true,
          customIcon: askHubConnected && (
            <StatusDot isConnected={askHubConnected} />
          ),
        }}
        title="AskRelayHub (Ask Session Events)"
        extras={{ defaultExpanded: askHubConnected ? true : false }}
      >
        <Section>
          <SectionTitle>AskRelayHub</SectionTitle>
          <p
            style={{
              color: '#6b7280',
              marginBottom: '16px',
              fontSize: '14px',
            }}
          >
            This hub handles Ask session message-level events such as message
            received/completed, document context, audience, tokens, and intents.
            Use the filters below to limit events to a specific workspace and
            ask session.
          </p>

          <FormGroup>
            <Label>Workspace ID Filter (optional)</Label>
            <Input
              type="text"
              value={askWorkspaceIdFilter}
              onChange={(e) => setAskWorkspaceIdFilter(e.target.value)}
              placeholder="Enter workspace ID to filter Ask events"
            />
          </FormGroup>

          <FormGroup>
            <Label>Ask Session ID Filter (optional)</Label>
            <Input
              type="text"
              value={askSessionIdFilter}
              onChange={(e) => setAskSessionIdFilter(e.target.value)}
              placeholder="Enter ask session ID to filter Ask events"
            />
          </FormGroup>

          <FormGroup>
            <Label>
              Subscribed Ask Events ({askRelayMessages.length} messages
              received)
            </Label>
            <div
              style={{
                fontSize: '12px',
                color: '#6b7280',
                marginBottom: '8px',
              }}
            >
              Listening for: {Object.values(AskRelayHubEvents).join(', ')}
            </div>
          </FormGroup>

          {askRelayMessages.length > 0 ? (
            <MessageLog>
              {askRelayMessages.map((msg, index) => {
                return (
                  <MessageEntry key={index}>
                    <MessageHeader>
                      <span>{msg.event}</span>
                      <MessageTime>{msg.timestamp}</MessageTime>
                    </MessageHeader>
                    {msg.parsed ? (
                      <div>
                        <div style={{ marginBottom: '8px' }}>
                          <strong>Parsed Data:</strong>
                          <div
                            style={{
                              marginTop: '4px',
                              padding: '8px',
                              background: '#f0f9ff',
                              borderRadius: '4px',
                            }}
                          >
                            {msg.parsed.workspaceId &&
                              msg.parsed.workspaceId !== 'unknown' && (
                                <div
                                  style={{
                                    marginBottom: '4px',
                                    color: '#1e40af',
                                  }}
                                >
                                  <strong>Workspace ID:</strong>{' '}
                                  <code
                                    style={{
                                      background: '#dbeafe',
                                      padding: '2px 4px',
                                      borderRadius: '2px',
                                    }}
                                  >
                                    {msg.parsed.workspaceId}
                                  </code>
                                </div>
                              )}
                            {msg.parsed.askSessionId &&
                              msg.parsed.askSessionId !== 'unknown' && (
                                <div
                                  style={{
                                    marginBottom: '4px',
                                    color: '#7c3aed',
                                  }}
                                >
                                  <strong>Ask Session ID:</strong>{' '}
                                  <code
                                    style={{
                                      background: '#ede9fe',
                                      padding: '2px 4px',
                                      borderRadius: '2px',
                                    }}
                                  >
                                    {msg.parsed.askSessionId}
                                  </code>
                                </div>
                              )}
                            {msg.parsed.messageId &&
                              msg.parsed.messageId !== 'unknown' && (
                                <div
                                  style={{
                                    marginBottom: '4px',
                                    color: '#059669',
                                  }}
                                >
                                  <strong>Message ID:</strong>{' '}
                                  <code
                                    style={{
                                      background: '#d1fae5',
                                      padding: '2px 4px',
                                      borderRadius: '2px',
                                    }}
                                  >
                                    {msg.parsed.messageId}
                                  </code>
                                </div>
                              )}
                            {typeof msg.parsed.tokens === 'number' && (
                              <div
                                style={{
                                  marginBottom: '4px',
                                  color: '#b45309',
                                }}
                              >
                                <strong>Tokens:</strong> {msg.parsed.tokens}
                              </div>
                            )}
                            {msg.parsed.intent && (
                              <div
                                style={{
                                  marginBottom: '4px',
                                  color: '#7c3aed',
                                }}
                              >
                                <strong>Intent:</strong> {msg.parsed.intent}
                              </div>
                            )}
                            {/* Show any other parsed fields */}
                            {Object.entries(msg.parsed).map(([key, value]) => {
                              if (
                                [
                                  'workspaceId',
                                  'askSessionId',
                                  'messageId',
                                  'tokens',
                                  'intent',
                                  'param1',
                                ].includes(key)
                              ) {
                                return null;
                              }
                              return (
                                <div key={key} style={{ marginBottom: '4px' }}>
                                  <strong>{key}:</strong>{' '}
                                  {typeof value === 'object'
                                    ? JSON.stringify(value)
                                    : String(value)}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <details style={{ marginTop: '8px' }}>
                          <summary
                            style={{
                              cursor: 'pointer',
                              color: '#6b7280',
                              fontSize: '11px',
                            }}
                          >
                            View Raw Data
                          </summary>
                          <MessageContent style={{ marginTop: '8px' }}>
                            {JSON.stringify(msg.data, null, 2)}
                          </MessageContent>
                        </details>
                      </div>
                    ) : (
                      <MessageContent>
                        {JSON.stringify(msg.data, null, 2)}
                      </MessageContent>
                    )}
                  </MessageEntry>
                );
              })}
            </MessageLog>
          ) : (
            <div
              style={{
                padding: '24px',
                textAlign: 'center',
                color: '#9ca3af',
                fontStyle: 'italic',
              }}
            >
              No Ask events received yet. Join appropriate Ask groups and
              trigger session messages to see them here.
            </div>
          )}
        </Section>
      </Accordion>

      {/* Custom Hub Invoke (Advanced) */}
      <Section>
        <SectionTitle>Custom Hub Invoke (Advanced)</SectionTitle>
        <p
          style={{
            color: '#6b7280',
            marginBottom: '16px',
            fontSize: '14px',
          }}
        >
          Manually invoke any SignalR hub method for debugging. Provide the hub
          name, method name, and arguments as JSON. Results and errors will be
          displayed below.
        </p>

        <FormGroup>
          <Label>Hub Name</Label>
          <Input
            type="text"
            value={customHubName}
            onChange={(e) => setCustomHubName(e.target.value)}
            placeholder='e.g. "workspaceRelayHub", "askRelayHub", "userRelayHub"'
          />
        </FormGroup>

        <FormGroup>
          <Label>Method Name</Label>
          <Input
            type="text"
            value={customMethodName}
            onChange={(e) => setCustomMethodName(e.target.value)}
            placeholder='e.g. "SendMessageAsync", "AddClientToUsersAsync"'
          />
        </FormGroup>

        <FormGroup>
          <Label>Arguments (JSON array or object)</Label>
          <TextArea
            value={customArgsText}
            onChange={(e) => setCustomArgsText(e.target.value)}
            placeholder='Examples: ["workspace-id","hello"] or {"workspaceId":"abc","message":"Hi"}'
          />
          <small style={{ color: '#6b7280' }}>
            Parsed with <code>JSON.parse</code>. Use an array for positional
            arguments, or an object to pass a single complex parameter.
          </small>
        </FormGroup>

        <ButtonGroup>
          <Button onClick={handleCustomInvoke} disabled={isInvokingCustom}>
            {isInvokingCustom ? 'Invoking…' : 'Invoke'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setCustomInvokeResult(null);
              setCustomInvokeError(null);
            }}
          >
            Clear Result
          </Button>
        </ButtonGroup>

        {(customInvokeResult || customInvokeError) && (
          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              borderRadius: '4px',
              backgroundColor: '#111827',
              color: '#e5e7eb',
              fontFamily: 'monospace',
              fontSize: '13px',
              whiteSpace: 'pre-wrap',
            }}
          >
            {customInvokeError && (
              <div style={{ color: '#fecaca', marginBottom: '8px' }}>
                <strong>Error:</strong> {customInvokeError}
              </div>
            )}
            {customInvokeResult && (
              <div>
                <strong>Result:</strong>
                <pre style={{ whiteSpace: 'pre-wrap' }}>
                  {customInvokeResult}
                </pre>
              </div>
            )}
          </div>
        )}
      </Section>

      {/* User Relay Hub */}
      <Accordion
        icon={{
          active: true,
          customIcon: userHubConnected && (
            <StatusDot isConnected={userHubConnected} />
          ),
        }}
        title="UserRelayHub (Workspace/User Management Events)"
        extras={{ defaultExpanded: !userHubConnected ? true : false }}
      >
        <Section>
          <SectionTitle>UserRelayHub</SectionTitle>
          <p
            style={{
              color: '#6b7280',
              marginBottom: '16px',
              fontSize: '14px',
            }}
          >
            This hub handles workspace and user management events like workspace
            creation, user additions/removals, role changes, and workspace
            updates. Subscribe to events to see real-time notifications.
          </p>

          <FormGroup>
            <Label>Workspace ID *</Label>
            <Input
              type="text"
              value={userRelayWorkspaceId}
              onChange={(e) => setUserRelayWorkspaceId(e.target.value)}
              placeholder="Enter workspace ID"
            />
          </FormGroup>

          <ButtonGroup>
            <Button
              variant="primary"
              onClick={handleAddUserRelayClientToGroup}
              disabled={!userHubConnected}
            >
              Add Client to Group
            </Button>
            <Button
              variant="secondary"
              onClick={() => setUserRelayMessages([])}
              disabled={userRelayMessages.length === 0}
            >
              Clear Messages
            </Button>
          </ButtonGroup>

          <hr
            style={{
              margin: '24px 0',
              border: 'none',
              borderTop: '1px solid #e5e7eb',
            }}
          />

          <FormGroup>
            <Label>
              Subscribed Events ({userRelayMessages.length} messages received)
            </Label>
            <div
              style={{
                fontSize: '12px',
                color: '#6b7280',
                marginBottom: '8px',
              }}
            >
              Listening for: {Object.values(UserRelayHubEvents).join(', ')}
            </div>
          </FormGroup>

          {userRelayMessages.length > 0 ? (
            <MessageLog>
              {userRelayMessages.map((msg, index) => {
                return (
                  <MessageEntry key={index}>
                    <MessageHeader>
                      <span>{msg.event}</span>
                      <MessageTime>{msg.timestamp}</MessageTime>
                    </MessageHeader>
                    {msg.parsed ? (
                      <div>
                        <div style={{ marginBottom: '8px' }}>
                          <strong>Parsed Data:</strong>
                          <div
                            style={{
                              marginTop: '4px',
                              padding: '8px',
                              background: '#f0f9ff',
                              borderRadius: '4px',
                            }}
                          >
                            {msg.parsed.workspaceId &&
                              msg.parsed.workspaceId !== 'unknown' && (
                                <div
                                  style={{
                                    marginBottom: '4px',
                                    color: '#1e40af',
                                  }}
                                >
                                  <strong>Workspace ID:</strong>{' '}
                                  <code
                                    style={{
                                      background: '#dbeafe',
                                      padding: '2px 4px',
                                      borderRadius: '2px',
                                    }}
                                  >
                                    {msg.parsed.workspaceId}
                                  </code>
                                </div>
                              )}
                            {msg.parsed.workspaceName && (
                              <div
                                style={{
                                  marginBottom: '4px',
                                  color: '#1e40af',
                                }}
                              >
                                <strong>Workspace Name:</strong>{' '}
                                {msg.parsed.workspaceName}
                              </div>
                            )}
                            {msg.parsed.userId &&
                              msg.parsed.userId !== 'unknown' && (
                                <div
                                  style={{
                                    marginBottom: '4px',
                                    color: '#7c3aed',
                                  }}
                                >
                                  <strong>User ID:</strong>{' '}
                                  <code
                                    style={{
                                      background: '#ede9fe',
                                      padding: '2px 4px',
                                      borderRadius: '2px',
                                    }}
                                  >
                                    {msg.parsed.userId}
                                  </code>
                                </div>
                              )}
                            {msg.parsed.userName && (
                              <div
                                style={{
                                  marginBottom: '4px',
                                  color: '#7c3aed',
                                }}
                              >
                                <strong>User Name:</strong>{' '}
                                {msg.parsed.userName}
                              </div>
                            )}
                            {/* Show any other parsed fields */}
                            {Object.entries(msg.parsed).map(([key, value]) => {
                              if (
                                [
                                  'workspaceId',
                                  'workspaceName',
                                  'userId',
                                  'userName',
                                  'param1',
                                ].includes(key)
                              ) {
                                return null;
                              }
                              return (
                                <div key={key} style={{ marginBottom: '4px' }}>
                                  <strong>{key}:</strong>{' '}
                                  {typeof value === 'object'
                                    ? JSON.stringify(value)
                                    : String(value)}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        <details style={{ marginTop: '8px' }}>
                          <summary
                            style={{
                              cursor: 'pointer',
                              color: '#6b7280',
                              fontSize: '11px',
                            }}
                          >
                            View Raw Data
                          </summary>
                          <MessageContent style={{ marginTop: '8px' }}>
                            {JSON.stringify(msg.data, null, 2)}
                          </MessageContent>
                        </details>
                      </div>
                    ) : (
                      <MessageContent>
                        {JSON.stringify(msg.data, null, 2)}
                      </MessageContent>
                    )}
                  </MessageEntry>
                );
              })}
            </MessageLog>
          ) : (
            <div
              style={{
                padding: '24px',
                textAlign: 'center',
                color: '#9ca3af',
                fontStyle: 'italic',
              }}
            >
              No messages received yet. Join a workspace group and trigger
              events to see them here.
            </div>
          )}
        </Section>
      </Accordion>
    </PageContainer>
  );
};
